import React, { useState, useRef, useEffect } from "react";
import logo from "../../assets/ieo.png";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ProgressBar,
  Badge,
  ListGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ZoomIn,
  Building,
  FileEarmarkText,
  CheckCircle,
  XCircle,
  InfoCircle,
  ArrowRight,
  Trophy,
  ShieldCheck,
  Clock,
  Award,
  PersonFill,
  EnvelopeFill,
  TelephoneFill,
  CalendarCheck,
  Globe,
  CashCoin,
  Book,
  FileEarmarkCheck,
  GraphUp,
  StarFill,
  ChatDots,
  Download,
  Share,
  FileEarmarkPdf,
  Check,
  X,
  FileText,
  CheckLg,
  XLg,
  QuestionCircle,
  HouseExclamation,
} from "react-bootstrap-icons";
import api from "../../services/api";
import jsPDF from "jspdf";

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    totalScore = 0,
    probability = {},
    studentId,
    studentInfo = {},
    answers = {},
    questions = [],
  } = location.state || {};

  const [showTips, setShowTips] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [detailedAnswers, setDetailedAnswers] = useState([]);
  const [maxPossibleScore, setMaxPossibleScore] = useState(91); // Based on your total of 91 points
  const [actualScore, setActualScore] = useState(totalScore);
  const [categoryBreakdown, setCategoryBreakdown] = useState({});

  // Color palette
  const colors = {
    background: "#032530",
    cardBg: "#0b2c36",
    text: "#FFFFFF",
    accent: "#ff8214",
    accentHover: "#ff9b44",
    border: "#1e273d",
    progressBg: "#0b2c36",
    success: "#018744",
    warning: "#ffaa00",
    danger: "#ff4757",
    info: "#3498db",
    purple: "#9b59b6",
  };

  // Constants
  const MAX_TOTAL_SCORE = 91; // Updated to match your total of 91 points
  const HIGH_THRESHOLD = 73; // 80% of 91 = 73
  const MEDIUM_THRESHOLD = 46; // 50% of 91 = 46

  useEffect(() => {
    // Calculate actual score based on answers
    calculateActualScore();
    // Fetch detailed answers if not provided
    if (studentId && (!answers || Object.keys(answers).length === 0)) {
      fetchStudentAnswers();
    }
  }, []);

  const calculateActualScore = () => {
    let calculatedScore = 0;
    let categories = {
      basic: { score: 0, max: 0, questions: 0 },
      academic: { score: 0, max: 0, questions: 0 },
      language: { score: 0, max: 0, questions: 0 },
      employment: { score: 0, max: 0, questions: 0 },
      financial: { score: 0, max: 0, questions: 0 },
      documentation: { score: 0, max: 0, questions: 0 },
      travel: { score: 0, max: 0, questions: 0 },
      visa: { score: 0, max: 0, questions: 0 },
    };

    // If answers are provided, calculate from them
    if (answers && Object.keys(answers).length > 0) {
      Object.entries(answers).forEach(([questionId, answerData]) => {
        const question = questions.find((q) => q.questionId === questionId);
        if (question && answerData.points) {
          calculatedScore += answerData.points;

          // Update category breakdown
          if (categories[question.category]) {
            categories[question.category].score += answerData.points;
            categories[question.category].max += Math.max(
              ...question.options.map((opt) => opt.points),
            );
            categories[question.category].questions += 1;
          }
        }
      });

      setActualScore(calculatedScore);
      setCategoryBreakdown(categories);
    } else {
      // Use the passed totalScore if no detailed answers
      setActualScore(totalScore);
    }
  };

  const fetchStudentAnswers = async () => {
    try {
      const response = await api.get(`/students/${studentId}/assessment`);
      if (response.data.success && response.data.assessment) {
        // Process answers
        const answersObj = {};
        response.data.assessment.answers.forEach((answer) => {
          answersObj[answer.questionId] = {
            answer: answer.answer,
            points: answer.points,
          };
        });

        // Recalculate score
        let newScore = 0;
        Object.values(answersObj).forEach((answer) => {
          newScore += answer.points || 0;
        });

        setActualScore(newScore);
      }
    } catch (error) {
      console.error("Error fetching student answers:", error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= HIGH_THRESHOLD) return colors.success;
    if (score >= MEDIUM_THRESHOLD) return colors.warning;
    return colors.danger;
  };

  const getProbabilityLevel = (score) => {
    if (score >= HIGH_THRESHOLD)
      return {
        level: "High",
        color: colors.success,
        percentage: "73-91 points",
        icon: "🏆",
        message:
          "Excellent visa profile! Strong chances for Irish visa approval.",
        description:
          "Your profile demonstrates strong eligibility across all key assessment areas.",
        tips: [
          "Your financial documentation appears robust",
          "Academic qualifications meet Irish standards",
          "Strong ties to home country demonstrated",
          "Consider premium visa processing option",
        ],
      };
    if (score >= MEDIUM_THRESHOLD)
      return {
        level: "Medium",
        color: colors.warning,
        percentage: "46-72 points",
        icon: "📊",
        message: "Good potential with targeted improvements",
        description:
          "Your profile shows promise but could benefit from enhancement in certain areas.",
        tips: [
          "Strengthen financial documentation",
          "Consider additional supporting letters",
          "Review travel history gaps",
          "Consult with visa specialist",
        ],
      };
    return {
      level: "Low",
      color: colors.danger,
      percentage: "0-45 points",
      icon: "🔍",
      message: "Requires significant enhancement",
      description:
        "Your profile needs substantial improvement across multiple assessment criteria.",
      tips: [
        "Urgent need to improve financial proof",
        "Academic qualifications may need verification",
        "Consider preparatory courses",
        "Immediate consultation recommended",
      ],
    };
  };

  const probabilityData = getProbabilityLevel(actualScore);
  const scoreColor = getScoreColor(actualScore);
  const percentage = Math.round((actualScore / MAX_TOTAL_SCORE) * 80);

  // Calculate score breakdown by category
  // Calculate score breakdown by category - SIMPLIFIED VERSION
  const calculateCategoryBreakdown = () => {
    // Define categories with their weights (these add up to 100%)
    const categories = {
      academic: {
        label: "Academic Profile",
        icon: <Book />,
        weight: 30, // 30% of total importance
        description: "Academic qualifications and language proficiency",
      },
      financial: {
        label: "Financial Capacity",
        icon: <CashCoin />,
        weight: 40, // 40% of total importance
        description: "Financial stability and funding proof",
      },
      travel: {
        label: "Immigration History",
        icon: <Globe />,
        weight: 10, // 10% of total importance
        description: "Previous travel and compliance record",
      },
      documentation: {
        label: "Documentation",
        icon: <FileEarmarkPdf />,
        weight: 15, // 15% of total importance
        description: "Completeness and quality of documents",
      },
      accommodation: {
        label: "Accommodation",
        icon: <HouseExclamation />,
        weight: 5, // 5% of total importance
        description: "Accommodation arrangements and proof",
      },
    };

    // We'll calculate the student's performance in each category as a percentage
    // of the maximum possible points for that category
    let academicScore = 0;
    let academicMax = 0;
    let financialScore = 0;
    let financialMax = 0;
    let travelScore = 0;
    let travelMax = 0;
    let documentationScore = 0;
    let documentationMax = 0;
    let accommodationScore = 0;
    let accommodationMax = 0;

    // Calculate scores from answers if available
    if (answers && Object.keys(answers).length > 0) {
      // Define question-to-category mapping
      const questionCategoryMap = {
        q1: "academic",
        q2: "academic",
        q3: "academic",
        q5: "academic",
        q7: "academic",
        q18: "academic",
        q8: "financial",
        q9: "financial",
        q10: "financial",
        q12: "financial",
        q14: "financial",
        q15: "travel",
        q17: "travel",
        q19: "travel",
        q11: "documentation",
        q13: "documentation",
        q20: "documentation",
        // Add accommodation questions here when you have them
        // q21: "accommodation",
        // q22: "accommodation",
      };

      // Calculate scores for each category
      Object.entries(answers).forEach(([questionId, answerData]) => {
        const points = answerData.points || 0;
        const category = questionCategoryMap[questionId];
        const question = questions.find((q) => q.questionId === questionId);

        if (question) {
          const maxPoints = Math.max(
            ...question.options.map((opt) => opt.points || 0),
          );

          switch (category) {
            case "academic":
              academicScore += points;
              academicMax += maxPoints;
              break;
            case "financial":
              financialScore += points;
              financialMax += maxPoints;
              break;
            case "travel":
              travelScore += points;
              travelMax += maxPoints;
              break;
            case "documentation":
              documentationScore += points;
              documentationMax += maxPoints;
              break;
            case "accommodation":
              accommodationScore += points;
              accommodationMax += maxPoints;
              break;
            default:
              break;
          }
        }
      });
    }

    // Calculate percentage for each category (how well they did in that category)
    const academicPercentage =
      academicMax > 0 ? (academicScore / academicMax) * 100 : 0;
    const financialPercentage =
      financialMax > 0 ? (financialScore / financialMax) * 100 : 0;
    const travelPercentage =
      travelMax > 0 ? (travelScore / travelMax) * 100 : 0;
    const documentationPercentage =
      documentationMax > 0 ? (documentationScore / documentationMax) * 100 : 0;
    const accommodationPercentage =
      accommodationMax > 0 ? (accommodationScore / accommodationMax) * 100 : 0;

    // Return breakdown array showing weight and performance
    return [
      {
        label: categories.academic.label,
        performancePercentage: Math.round(academicPercentage),
        weight: categories.academic.weight,
        weightLabel: "30%",
        icon: categories.academic.icon,
        description: categories.academic.description,
        score: academicScore,
        max: academicMax,
      },
      {
        label: categories.financial.label,
        performancePercentage: Math.round(financialPercentage),
        weight: categories.financial.weight,
        weightLabel: "40%",
        icon: categories.financial.icon,
        description: categories.financial.description,
        score: financialScore,
        max: financialMax,
      },
      {
        label: categories.travel.label,
        performancePercentage: Math.round(travelPercentage),
        weight: categories.travel.weight,
        weightLabel: "10%",
        icon: categories.travel.icon,
        description: categories.travel.description,
        score: travelScore,
        max: travelMax,
      },
      {
        label: categories.documentation.label,
        performancePercentage: Math.round(documentationPercentage),
        weight: categories.documentation.weight,
        weightLabel: "15%",
        icon: categories.documentation.icon,
        description: categories.documentation.description,
        score: documentationScore,
        max: documentationMax,
      },
      {
        label: categories.accommodation.label,
        performancePercentage: Math.round(accommodationPercentage),
        weight: categories.accommodation.weight,
        weightLabel: "5%",
        icon: categories.accommodation.icon,
        description: categories.accommodation.description,
        score: accommodationScore,
        max: accommodationMax,
      },
    ];
  };

  const scoreBreakdown = calculateCategoryBreakdown();

  // Success rate comparison based on 91-point scale
  const successRateComparison = [
    {
      range: "73-91",
      rate: "85%",
      description: "High probability applicants",
      threshold: HIGH_THRESHOLD,
    },
    {
      range: "46-72",
      rate: "65%",
      description: "Medium probability applicants",
      threshold: MEDIUM_THRESHOLD,
    },
    {
      range: "0-45",
      rate: "30%",
      description: "Low probability applicants",
      threshold: 0,
    },
  ];

  // Answer details for display
  const getAnswerDetails = () => {
    if (!answers || Object.keys(answers).length === 0) return [];

    const answerDetails = [];

    // Map of all questions with their options
    const questionMap = {
      q1: {
        text: "What level are you applying for?",
        options: {
          pg: "Postgraduate (PG) - 5 points",
          ug: "Undergraduate (UG) - 4 points",
          esol_others: "ESOL / Others - 0 points",
        },
      },
      q2: {
        text: "What is the title of degree you are going for in Ireland?",
        options: {
          masters: "Masters - 5 points",
          higher_diploma: "Higher diploma - 3 points",
          undergraduate: "Undergraduate - 4 points",
          foundation: "Foundation / pre masters / Top up year - 2 points",
        },
      },
      q3: {
        text: "When did you complete your last qualification?",
        options: {
          within_6_months: "Within last 6 months - 5 points",
          "6m_2_years": "6 months - 2 years - 4 points",
          "3_5_years": "3-5 years ago - 3 points",
          more_than_5: "More than 5 years - 2 points",
        },
      },
      q4: {
        text: "Do you have an English language test or planning to attempt?",
        options: {
          yes_with_score:
            "Yes (IELTS or equivalent with required score) - 5 points",
          planning_to_attempt: "No, planning to attempt soon - 5 points",
          no: "No, not yet / I don't want to attempt - 0 points",
        },
      },
      q5: {
        text: "Do you have any study gaps?",
        options: {
          no_gap: "No gap - 5 points",
          gap_with_docs: "Gap with documentation - 4 points",
          gap_without_docs: "Gap without documentation - 3 points",
        },
      },
      q6: {
        text: "Are you currently working?",
        options: {
          working: "Yes, working - 4 points",
          fresh_graduate: "No, freshly graduated - 5 points",
          not_working: "Not working (gaps) - 3 points",
        },
      },
      q7: {
        text: "Course relevance to previous education/work?",
        options: {
          strongly_related: "Strongly related - 5 points",
          partially_related: "Partially related - 4 points",
          not_related: "Not related - 1 point",
        },
      },
      q8: {
        text: "Who is sponsoring your education?",
        options: {
          self_sponsored: "Self-sponsored - 5 points",
          parents: "Parents - 5 points",
          relative_guardian: "Relative/Guardian - 3 points",
        },
      },
      q9: {
        text: "Your/Sponsor's Annual income range? (EUR)",
        options: {
          "30k_plus": "Annual income EUR 30,000+ - 5 points",
          "20k_plus": "Annual income EUR 20,000+ - 4 points",
          "10k_plus": "Annual income EUR 10,000+ - 3 points",
        },
      },

      q10: {
        text: "Do you have sufficient funds in bank account of student / sponsor, to cover the full course fee and living costs?",
        options: {
          full_duration:
            "Yes - Full course duration funds available (tuition + living expenses) - 5 points",
          partial: "Partial funds available - 4 points",
          insufficient:
            "No - Insufficient funds to cover required duration - 2 points",
        },
      },
      q11: {
        text: "Will you be able to submit financial supporting documents to any unexplained Lump sum?",
        options: {
          yes_full_docs:
            "Yes - I can submit sponsor last 6 month Bank St plus source of income - 5 points",
          partial_docs:
            "No - My sponsor cannot provide 6 month Bank St / full source of income - 3 points",
          no_docs: "No - My sponsor cannot provide any documents - 0 points",
        },
      },
      q12: {
        text: "Can you pay the required tuition fee for your selected course?",
        options: {
          full_payment: "Full 1st year fee payment - 5 points",
          partial_payment: "Partial 1st year fee payment - 3 points",
        },
      },
      q13: {
        text: "Do your sponsor have Tax returns (NTN and return copy) of last year?",
        options: {
          yes: "Yes - 5 points",
          no_returns: "No returns - 2 points",
          exempt: "Exempt (e.g. agricultural income) - 5 points",
        },
      },
      q14: {
        text: "Do you have any property or registered business in your name?",
        options: {
          property: "Yes - Property registered in my name - 4 points",
          business: "Yes - Registered business in my name - 4 points",
          both: "Yes - Both property and registered business in my name - 5 points",
          none: "No - I do not have any - 0 points",
        },
      },
      q15: {
        text: "International travel history - what is the status?",
        options: {
          western_travel:
            "UK / EU / USA / Canada travel with lawful return - 5 points",
          other_travel: "Any international travel (other countries) - 3 points",
          no_travel: "No travel history - 2 points",
        },
      },
      q16: {
        text: "Have you ever had a previous visa application refused, or been subject to any immigration-related bans or restrictions?",
        options: {
          no_refusals: "No - Never had any - 5 points",
          general_refusal: "Yes - General visa refusal - 4 points",
          fraud_banned: "Yes - Visa refusal due to fraud / banned - 0 points",
        },
      },
      q17: {
        text: "Have you ever travelled to the UK, and do you have any negative immigration history?",
        options: {
          never_travelled_uk: "Never travelled to UK - 3 points",
          travel_no_issues: "Travel, no issues - 3 points",
          negative_history:
            "Any negative history (overstay/refusal/ban/refusal of PSW visa) - 0 points",
        },
      },
      q18: {
        text: "Are you currently studying in the UK on a study visa?",
        options: {
          uk_masters: "Yes, currently studying in masters program - 3 points",
          uk_undergraduate:
            "Yes, currently studying in Undergraduate program - 4 points",
          never_studied_uk: "Never studied in the UK - 0 points",
        },
      },
      q19: {
        text: "When is your UK Post-Study Work (PSW) visa expiring?",
        options: {
          expired_or_3months:
            "Already expired / within coming 3 months - 0 points",
          expiring_6months: "Expiring within the next 6 months - 1 point",
          expiring_6plus_months: "Expiring in more than 6 months - 2 points",
          still_studying: "Still studying - 3 points",
        },
      },
      q20: {
        text: "Is your name spelled consistently across all your official documents?",
        options: {
          exact_match: "Yes - Exactly the same on all documents - 5 points",
          minor_variations:
            "No - Minor variations (e.g., middle name, initials) - 4 points",
          major_discrepancies:
            "No - Major discrepancies between documents - 2 points",
        },
      },
    };

    // Create answer details array
    Object.entries(answers).forEach(([questionId, answerData]) => {
      const question = questionMap[questionId];
      if (question) {
        const optionText =
          question.options[answerData.answer] || answerData.answer;
        answerDetails.push({
          questionId,
          questionText: question.text,
          answer: optionText,
          points: answerData.points || 0,
          maxPoints: Math.max(
            ...Object.values(question.options).map((opt) => {
              const match = opt.match(/- (\d+) points?$/);
              return match ? parseInt(match[1]) : 0;
            }),
          ),
        });
      }
    });

    return answerDetails.sort((a, b) => {
      const numA = parseInt(a.questionId.replace("q", ""));
      const numB = parseInt(b.questionId.replace("q", ""));
      return numA - numB;
    });
  };

  const answerDetails = getAnswerDetails();

  // Recommendations based on actual score and answers
  const recommendations = [
    {
      text: "Submit clear and consistent financial documentation",
      icon: <CheckCircle color={colors.success} />,
      category: "financial",
      priority: "High",
    },
    {
      text: "Declare complete and truthful previous travel history without omission",
      icon: <CheckCircle color={colors.success} />,
      category: "financial",
      priority: "High",
    },
    {
      text: "Prepare a strong SOP explain course and Ireland as a study destination",
      icon: <CheckCircle color={colors.success} />,
      category: "academic",
      priority: "High",
    },
    {
      text: "Clearly outline post-study plans and intent to return home.",
      icon: <CheckCircle color={colors.success} />,
      category: "documents",
      priority: "High",
    },
    {
      text: "Ensure all documents are complete, error-free and accurate",
      icon: <CheckCircle color={colors.success} />,
      category: "visa",
      priority: "High",
    },
  ];

  const handleOptionSelect = async (option) => {
    try {
      if (option === 3) {
        const response = await api.post("/appointments", {
          studentId: studentId,
          appointmentType: "assessment",
          status: "pending",
        });

        if (response.data.success) {
          navigate(`/documents/${response.data.appointment.appointmentId}`);
        }
      } else {
        navigate("/booking", {
          state: {
            studentId: studentId,
            type: option === 1 ? "zoom" : "office",
          },
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Format phone number
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.replace(/(\d{4})(\d{7})/, "$1 $2");
  };

  // Generate PDF Report
  const generatePDF = async () => {
    setGeneratingPDF(true);
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // A4 dimensions
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10; // Reduced from 15
      const contentWidth = pageWidth - 2 * margin;

      // Color palette - Darker for better print
      const pdfColors = {
        primary: [3, 37, 48], // Dark blue
        accent: [255, 130, 20], // Orange
        text: [0, 0, 0], // Black for better readability
        textLight: [100, 100, 100], // Gray for less important text
      };

      // Set default font - smaller sizes for compact layout
      pdf.setFont("helvetica");

      let yPos = margin;

      // ==================== HEADER WITH LOGO ====================
      try {
        // Add logo at the top center
        const logoWidth = 65;
        const logoHeight = 15;
        var logoX = (pageWidth - logoWidth) / 2; // Center the logo

        // Add the logo image
        const logoData = await getLogoDataURL(); // Function to get logo as data URL
        pdf.addImage(logoData, "PNG", logoX, yPos, logoWidth, logoHeight);

        yPos += logoHeight + 5; // Add space after logo
      } catch (error) {
        console.log("Logo not loaded, using text fallback");
        // If logo fails to load, use text header
        pdf.setFontSize(16);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(
          pdfColors.primary[0],
          pdfColors.primary[1],
          pdfColors.primary[2],
        );
        pdf.text("IRELAND EDUCATION OFFICE", pageWidth / 2, yPos + 10, {
          align: "center",
        });
        yPos += 20;
      }

      // Report Title
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(
        pdfColors.primary[0],
        pdfColors.primary[1],
        pdfColors.primary[2],
      );
      pdf.text("VISA ASSESSMENT REPORT", pageWidth / 2, yPos, {
        align: "center",
      });

      // Report details
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(
        pdfColors.textLight[0],
        pdfColors.textLight[1],
        pdfColors.textLight[2],
      );
      pdf.text(
        `Generated: ${new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })} | Ref: ${studentId?.slice(-8) || "STU" + Date.now().toString().slice(-6)}`,
        pageWidth / 2,
        yPos + 7,
        { align: "center" },
      );

      yPos += 15;

      // Horizontal line separator
      pdf.setDrawColor(
        pdfColors.accent[0],
        pdfColors.accent[1],
        pdfColors.accent[2],
      );
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;

      // ==================== APPLICANT INFO - COMPACT TABLE ====================
      const applicantCol1 = [
        `Applicant: ${studentInfo.name || "Not provided"}`,
        `Email: ${studentInfo.email || "Not provided"}`,
      ];

      const applicantCol2 = [
        `Success Rate: ${percentage}%`,
        `Phone: ${studentInfo.phone || "Not provided"}`,
      ];

      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(pdfColors.text[0], pdfColors.text[1], pdfColors.text[2]);

      // Two column layout for applicant info
      applicantCol1.forEach((info, index) => {
        pdf.text(info, margin, yPos + index * 5);
      });

      applicantCol2.forEach((info, index) => {
        pdf.text(info, margin + 100, yPos + index * 5);
      });

      yPos += 20;

      // ==================== SCORE VISUALIZATION ====================
      // Main score with color
      const levelRGB = hexToRgb(probabilityData.color || "#ff8214");

      // pdf.setFontSize(36);
      // pdf.setFont("helvetica", "bold");
      // pdf.setTextColor(levelRGB.r, levelRGB.g, levelRGB.b);
      // pdf.text(`${actualScore}`, margin, yPos + 15);

      // pdf.setFontSize(14);
      // pdf.setTextColor(pdfColors.text[0], pdfColors.text[1], pdfColors.text[2]);
      // pdf.text(`/ ${MAX_TOTAL_SCORE}`, margin + 25, yPos + 15);

      // Probability message beside score
      pdf.setFontSize(24);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(
        pdfColors.accent[0],
        pdfColors.accent[1],
        pdfColors.accent[2],
      );
      pdf.text(`${percentage}% SUCCESS PROBABILITY`, margin, yPos + 8);

      // pdf.setFontSize(10);
      // pdf.setFont("helvetica", "normal");
      // pdf.setTextColor(pdfColors.text[0], pdfColors.text[1], pdfColors.text[2]);
      // const shortMessage =
      //   probabilityData.message?.substring(0, 80) +
      //   (probabilityData.message?.length > 80 ? "..." : "");
      // pdf.text(shortMessage, margin + 60, yPos + 15);

      yPos += 25;

      // ==================== RECOMMENDATIONS - COMPACT LIST ====================
      if (recommendations.length > 0) {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(
          pdfColors.primary[0],
          pdfColors.primary[1],
          pdfColors.primary[2],
        );
        pdf.text("KEY RECOMMENDATIONS:", margin, yPos);

        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(
          pdfColors.text[0],
          pdfColors.text[1],
          pdfColors.text[2],
        );

        // Show all recommendations in compact format
        recommendations.forEach((rec, index) => {
          if (index < 4) {
            // Limit to 4 recommendations
            const y = yPos + 5 + index * 4.5;
            const shortText =
              rec.text.substring(0, 100) + (rec.text.length > 100 ? "..." : "");
            pdf.text(`• ${shortText}`, margin + 2, y);
          }
        });

        yPos += 25;
      }

      // Horizontal line separator
      pdf.setDrawColor(
        pdfColors.accent[0],
        pdfColors.accent[1],
        pdfColors.accent[2],
      );
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      // ==================== NEXT STEPS - 3 COLUMN LAYOUT ====================
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(
        pdfColors.primary[0],
        pdfColors.primary[1],
        pdfColors.primary[2],
      );
      pdf.text("CONSULTATION OPTIONS:", margin, yPos);
      yPos += 6;

      const nextSteps = [
        {
          title: "Call Back",
          description: "Request a Call Back",
          features: ["Instant booking", "Basic review"],
          price: "FREE",
          highlight: false,
        },
        {
          title: "OFFICE VISIT",
          description: "45-min in-person discussion",
          features: ["Document analysis", "Detailed review"],
          price: "FREE",
          highlight: false,
        },
      ];

      const stepColWidth = contentWidth / 3;
      const stepStartY = yPos;

      nextSteps.forEach((step, index) => {
        const colX = margin + index * stepColWidth;
        const colCenter = colX + stepColWidth / 2;

        // Title
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(
          pdfColors.accent[0],
          pdfColors.accent[1],
          pdfColors.accent[2],
        );
        pdf.text(step.title, colCenter, stepStartY + 5, { align: "center" });

        // Description
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(
          pdfColors.text[0],
          pdfColors.text[1],
          pdfColors.text[2],
        );
        const descLines = pdf.splitTextToSize(
          step.description,
          stepColWidth - 10,
        );
        pdf.text(descLines, colCenter, stepStartY + 11, { align: "center" });

        // Features with bullets
        let featureY = stepStartY + 18;
        step.features.forEach((feature, featureIndex) => {
          pdf.setFontSize(7);
          pdf.text(`• ${feature}`, colCenter, featureY + featureIndex * 4, {
            align: "center",
          });
        });

        // Price
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(
          step.highlight ? pdfColors.accent[0] : pdfColors.primary[0],
          step.highlight ? pdfColors.accent[1] : pdfColors.primary[1],
          step.highlight ? pdfColors.accent[2] : pdfColors.primary[2],
        );
        pdf.text(step.price, colCenter, stepStartY + 30, { align: "center" });
      });

      yPos += 45;

      // ==================== OFFICE DETAILS - COMPACT 3 COLUMN ====================
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(
        pdfColors.primary[0],
        pdfColors.primary[1],
        pdfColors.primary[2],
      );
      pdf.text("OUR OFFICES:", margin, yPos);
      yPos += 6;

      const offices = [
        {
          city: "LAHORE",
          address: "12 Aibek Block, New Garden Town",
          landmark: "Near Kaizen High School",
          contacts: ["+92 308 8881287", "+92 331 4220805", "+92 316 4798104"],
          email: "info@educationireland.net",
        },
        {
          city: "ISLAMABAD",
          address: "2nd Floor West Wing, Asif Plaza",
          landmark: "106-E Fazal-Ul-Haq Road, Blue Area",
          contacts: ["+92 316 4488201", "+92 318 4366632"],
          email: "Islamabad@educationireland.net",
        },
        {
          city: "KARACHI",
          address: "Office 503, 5th Floor, Elegant Tower",
          landmark: "Plot BC 2, Block 5 Clifton",
          contacts: ["+92 310 4263667", "+92 308 8880137"],
          email: "karachi@educationireland.net",
        },
      ];

      const officeColWidth = contentWidth / 3;
      const officeStartY = yPos;

      offices.forEach((office, index) => {
        const colX = margin + index * officeColWidth;
        const colCenter = colX + officeColWidth / 2;

        // City
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(
          pdfColors.accent[0],
          pdfColors.accent[1],
          pdfColors.accent[2],
        );
        pdf.text(office.city, colCenter, officeStartY + 5, { align: "center" });

        // Address
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(
          pdfColors.text[0],
          pdfColors.text[1],
          pdfColors.text[2],
        );

        const addressLines = pdf.splitTextToSize(
          office.address,
          officeColWidth - 8,
        );
        pdf.text(addressLines, colCenter, officeStartY + 10, {
          align: "center",
        });

        const landmarkLines = pdf.splitTextToSize(
          office.landmark,
          officeColWidth - 8,
        );
        pdf.text(landmarkLines, colCenter, officeStartY + 15, {
          align: "center",
        });

        // Contact numbers (show first 2 only)
        pdf.setFont("helvetica", "bold");
        pdf.text("Contact:", colCenter, officeStartY + 21, { align: "center" });

        pdf.setFont("helvetica", "normal");
        office.contacts.slice(0, 2).forEach((contact, contactIndex) => {
          pdf.text(contact, colCenter, officeStartY + 25 + contactIndex * 4, {
            align: "center",
          });
        });

        // Email
        pdf.setFont("helvetica", "bold");
        pdf.text("Email:", colCenter, officeStartY + 34, { align: "center" });

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(6);
        const emailLines = pdf.splitTextToSize(
          office.email,
          officeColWidth - 8,
        );
        pdf.text(emailLines, colCenter, officeStartY + 38, { align: "center" });
      });

      yPos += 55;

      // ==================== FOOTER ====================
      // Horizontal line
      pdf.setDrawColor(
        pdfColors.accent[0],
        pdfColors.accent[1],
        pdfColors.accent[2],
      );
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPos, pageWidth - margin, yPos);

      yPos += 5;

      // Website and confidentiality
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(
        pdfColors.accent[0],
        pdfColors.accent[1],
        pdfColors.accent[2],
      );
      pdf.text("www.educationireland.net", pageWidth / 2, yPos, {
        align: "center",
      });

      pdf.setFont("helvetica", "italic");
      pdf.setTextColor(
        pdfColors.textLight[0],
        pdfColors.textLight[1],
        pdfColors.textLight[2],
      );
      const note =
        "The outcomes provided by this assessment are intended for preliminary guidance only and do not constitute a guarantee of visa issuance or legal success. This platform operates as an independent consultancy and maintains no formal affiliation, endorsement, or partnership with the Government of Ireland or any statutory immigration authorities. • Page 1 of 1";

      pdf.text(note, pageWidth / 2, yPos + 5, {
        align: "center",
        maxWidth: pageWidth - 40, // keeps text inside margins
      });

      // ==================== SAVE PDF ====================
      const fileName = `Visa-Assessment-${
        studentInfo.name?.replace(/\s+/g, "-") || "Student"
      }-${new Date().toISOString().split("T")[0]}.pdf`;

      // Save the PDF
      pdf.save(fileName);

      // Create blob URL for preview
      const pdfBlob = pdf.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);

      // Show preview modal
      setShowPDFPreview(true);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Helper function to get logo as data URL
  const getLogoDataURL = async () => {
    try {
      // Import the logo from assets
      const logo = await import("../../assets/ieo.png");
      return logo.default;
    } catch (error) {
      console.error("Error loading logo:", error);
      throw error;
    }
  };
  // Helper function to convert hex to RGB
  const hexToRgb = (hex) => {
    // Remove # if present
    hex = hex.replace(/^#/, "");

    // Parse the hex color
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return { r, g, b };
  };

  // Handle download button click
  const handleDownloadPDF = () => {
    generatePDF();
  };

  // PDF Preview Modal
  const PDFPreviewModal = () => (
    <Modal
      show={showPDFPreview}
      onHide={() => setShowPDFPreview(false)}
      size="lg"
      centered
      style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
    >
      <Modal.Header
        closeButton
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
        }}
      >
        <Modal.Title
          style={{
            color: colors.text,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FileEarmarkPdf style={{ color: colors.accent }} />
          PDF Report Generated Successfully
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ backgroundColor: colors.background, textAlign: "center" }}
      >
        <div style={{ marginBottom: "20px" }}>
          <CheckCircle size={80} color={colors.success} />
        </div>
        <h4 style={{ color: colors.text, marginBottom: "10px" }}>
          Your visa assessment report has been downloaded!
        </h4>
        <p style={{ color: colors.text, opacity: 0.8, marginBottom: "20px" }}>
          A detailed PDF report has been saved to your device. You can also view
          it online.
        </p>

        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            marginTop: "30px",
          }}
        >
          <Button
            variant="outline"
            style={{
              borderColor: colors.accent,
              color: colors.accent,
              borderRadius: "8px",
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onClick={() => window.open(pdfUrl, "_blank")}
          >
            <FileText size={16} />
            View PDF in Browser
          </Button>
          <Button
            style={{
              backgroundColor: colors.accent,
              borderColor: colors.accent,
              borderRadius: "8px",
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onClick={() => setShowPDFPreview(false)}
          >
            <Check size={16} />
            Close
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );

  return (
    <div
      style={{
        backgroundColor: colors.background,
        minHeight: "100vh",
        paddingTop: "2rem",
        paddingBottom: "2rem",
      }}
    >
      <Container fluid>
        <Row>
          {/* Left Sidebar - Student Information */}
          <Col lg={4} xl={3} className="mb-4">
            <Card
              className="border-0 sticky-top"
              style={{
                backgroundColor: "#13181f",
                borderRadius: "20px",
                boxShadow: `0 10px 40px rgba(0, 0, 0, 0.3)`,
                border: `1px solid ${colors.border}`,
                top: "10rem",
                zIndex: 100,
              }}
            >
              <Card.Body style={{ padding: "1.75rem" }}>
                {/* Student Profile Header */}
                <div className="text-center mb-4">
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      backgroundColor: `${colors.accent}20`,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1rem",
                      border: `3px solid ${colors.accent}40`,
                    }}
                  >
                    <PersonFill size={36} style={{ color: colors.accent }} />
                  </div>
                  <h4
                    style={{
                      color: colors.text,
                      fontWeight: "700",
                      marginBottom: "0.25rem",
                    }}
                  >
                    {studentInfo.name || "Student Name"}
                  </h4>
                  <Badge
                    bg=""
                    style={{
                      backgroundColor: `${colors.accent}20`,
                      color: colors.accent,
                      fontSize: "0.8rem",
                      padding: "0.25rem 0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    Visa Applicant
                  </Badge>
                </div>

                {/* Score Summary */}
                <div className="mb-4">
                  <div
                    style={{
                      backgroundColor: colors.background,
                      borderRadius: "12px",
                      padding: "1rem",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <div className="text-center mb-3">
                      <div
                        style={{
                          fontSize: "2.5rem",
                          fontWeight: "800",
                          color: scoreColor,
                          lineHeight: "1",
                        }}
                      >
                        {percentage}%
                        <span
                          style={{
                            fontSize: "1rem",
                            color: colors.text,
                            opacity: 0.6,
                            marginLeft: "0.25rem",
                          }}
                        ></span>
                      </div>
                      <div
                        style={{
                          color: colors.text,
                          fontSize: "0.9rem",
                          fontWeight: "600",
                        }}
                      >
                        Success Probability
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="mb-4">
                  <h6
                    style={{
                      color: colors.text,
                      fontWeight: "600",
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <InfoCircle size={16} />
                    Contact Details
                  </h6>
                  <ListGroup variant="flush">
                    <ListGroup.Item
                      style={{
                        backgroundColor: "transparent",
                        borderColor: colors.border,
                        padding: "0.75rem 0",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <EnvelopeFill
                          size={16}
                          style={{ color: colors.accent, marginRight: "10px" }}
                        />
                        <div>
                          <small style={{ color: colors.text, opacity: 0.7 }}>
                            Email
                          </small>
                          <div style={{ color: colors.text }}>
                            {studentInfo.email}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item
                      style={{
                        backgroundColor: "transparent",
                        borderColor: colors.border,
                        padding: "0.75rem 0",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <TelephoneFill
                          size={16}
                          style={{ color: colors.accent, marginRight: "10px" }}
                        />
                        <div>
                          <small style={{ color: colors.text, opacity: 0.7 }}>
                            Phone
                          </small>
                          <div style={{ color: colors.text }}>
                            {studentInfo.phone}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  </ListGroup>
                </div>

                {/* Quick Actions */}
                <div>
                  <h6
                    style={{
                      color: colors.text,
                      fontWeight: "600",
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Share size={16} />
                    Quick Actions
                  </h6>
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline"
                      onClick={handleDownloadPDF}
                      disabled={generatingPDF}
                      style={{
                        borderColor: colors.accent,
                        color: colors.accent,
                        borderRadius: "10px",
                        padding: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        backgroundColor: `${colors.accent}10`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = colors.accentHover;
                        e.currentTarget.style.color = colors.accentHover;
                        e.currentTarget.style.backgroundColor = `${colors.accent}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = colors.accent;
                        e.currentTarget.style.color = colors.accent;
                        e.currentTarget.style.backgroundColor = `${colors.accent}10`;
                      }}
                    >
                      {generatingPDF ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          Generating PDF...
                        </>
                      ) : (
                        <>
                          <Download size={16} />
                          Download Report (PDF)
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Main Content Area */}
          <Col lg={8} xl={9}>
            {/* Header */}
            <div className="mb-4">
              <h1
                style={{
                  color: colors.text,
                  fontWeight: "800",
                  marginBottom: "0.5rem",
                  fontSize: "2.75rem",
                }}
              >
                Irish Visa Profile Assessment
              </h1>
              <p
                style={{
                  color: colors.text,
                  opacity: 0.8,
                  fontSize: "1.1rem",
                }}
              >
                Comprehensive analysis of your eligibility for Irish student
                visa
              </p>
            </div>

            {/* Main Score Card */}
            <Card
              className="border-0 mb-4"
              style={{
                backgroundColor: "#13181f",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: `0 10px 40px rgba(0, 0, 0, 0.3)`,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Card.Body style={{ padding: "2.5rem" }}>
                <Row className="align-items-center">
                  <Col>
                    {/* Score Display */}
                    <div className="text-center">
                      <div className="mb-4">
                        <div
                          style={{
                            color: scoreColor,
                            fontSize: "4.5rem",
                            marginBottom: "0.5rem",
                            fontWeight: "700",
                          }}
                        >
                          {percentage}%
                        </div>
                        <div
                          style={{
                            color: colors.accent,
                            fontWeight: "600",
                            fontSize: "1rem",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                          }}
                        >
                          Success Probability
                        </div>
                      </div>

                      {/* Probability Badge */}
                    </div>

                    {/* Disclaimer Note - Prominent Highlight */}
                    <div
                      style={{
                        fontSize: "0.85rem",
                        color: "#ffd700",
                        backgroundColor: "rgba(255, 215, 0, 0.1)",
                        textAlign: "center",
                        border: "1px solid rgba(255, 215, 0, 0.3)",
                        borderRadius: "8px",
                        padding: "1.25rem",
                        marginTop: "1.5rem",
                        lineHeight: "1.6",
                        fontWeight: "500",
                        letterSpacing: "0.3px",
                        boxShadow: "0 4px 12px rgba(255, 215, 0, 0.1)",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          fontSize: "0.9rem",
                          fontWeight: "700",
                          marginBottom: "0.5rem",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          color: "#ffd700",
                        }}
                      >
                        ⚠️ Important Disclaimer
                      </span>
                      The outcomes provided by this assessment are intended for
                      preliminary guidance only and do not constitute a
                      guarantee of visa issuance or legal success. This platform
                      operates as an independent consultancy and maintains no
                      formal affiliation, endorsement, or partnership with the
                      Government of Ireland or any statutory immigration
                      authorities.
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Detailed Analysis and Recommendations Section */}
            <Row className="mb-4">
              <Col lg={6}>
                {/* Score Breakdown Widget */}
                <Card
                  className="border-0 h-100"
                  style={{
                    backgroundColor: "#13181f",
                    borderRadius: "20px",
                    boxShadow: `0 8px 30px rgba(0, 0, 0, 0.3)`,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <Card.Body style={{ padding: "1.75rem" }}>
                    <h5
                      style={{
                        color: colors.text,
                        fontWeight: "600",
                        marginBottom: "1.5rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <GraphUp style={{ color: colors.accent }} />
                      Standard Weight of each category in Irish Visa
                    </h5>

                    {scoreBreakdown.map((item, index) => {
                      // The progress bar shows the WEIGHT percentage (30, 40, 10, 15, 5)
                      const weightPercentage = item.weight; // This is already 30, 40, 10, 15, 5

                      return (
                        <div key={index} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <div style={{ color: colors.accent }}>
                                {item.icon}
                              </div>
                              <div>
                                <span
                                  style={{
                                    color: colors.text,
                                    fontWeight: "500",
                                  }}
                                >
                                  {item.label}
                                </span>
                                <div
                                  style={{
                                    color: colors.text,
                                    opacity: 0.6,
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {item.description}
                                </div>
                              </div>
                            </div>
                            <div className="text-end">
                              <div
                                style={{
                                  color: colors.accent,
                                  fontSize: "0.9rem",
                                  fontWeight: "600",
                                }}
                              >
                                {item.weightLabel}
                              </div>
                            </div>
                          </div>

                          {/* Progress bar showing WEIGHT (importance) */}
                          <div
                            style={{
                              height: "10px",
                              backgroundColor: colors.border,
                              borderRadius: "5px",
                              overflow: "hidden",
                              position: "relative",
                              marginBottom: "4px",
                            }}
                          >
                            <div
                              style={{
                                width: `${weightPercentage}%`,
                                height: "100%",
                                background: `linear-gradient(90deg, ${colors.accent}80, ${colors.accent})`,
                                borderRadius: "5px",
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                {/* Recommendations Widget */}
                <Card
                  className="border-0 h-100"
                  style={{
                    backgroundColor: "#13181f",
                    borderRadius: "20px",
                    boxShadow: `0 8px 30px rgba(0, 0, 0, 0.3)`,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <Card.Body style={{ padding: "1.75rem" }}>
                    <h5
                      style={{
                        color: colors.text,
                        fontWeight: "600",
                        marginBottom: "1.5rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <ShieldCheck style={{ color: colors.accent }} />
                      Priority Recommendations
                    </h5>
                    <div
                      style={{
                        backgroundColor: colors.background,
                        borderRadius: "12px",
                        padding: "1rem",
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      {recommendations.map((rec, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-start mb-3 p-2"
                          style={{
                            backgroundColor: colors.cardBg,
                            borderRadius: "8px",
                            border: `1px solid ${colors.border}`,
                            transition: "all 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.borderColor = colors.accent;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.borderColor = colors.border;
                          }}
                        >
                          <div className="me-3" style={{ marginTop: "2px" }}>
                            {rec.icon}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                color: colors.text,
                                fontSize: "0.95rem",
                                lineHeight: "1.4",
                              }}
                            >
                              {rec.text}
                            </div>
                            <Badge
                              bg=""
                              style={{
                                backgroundColor:
                                  rec.priority === "High"
                                    ? colors.success + "30"
                                    : rec.priority === "Medium"
                                      ? colors.warning + "30"
                                      : colors.success + "30",
                                color:
                                  rec.priority === "High"
                                    ? colors.success
                                    : rec.priority === "Medium"
                                      ? colors.warning
                                      : colors.success,
                                fontSize: "0.7rem",
                                padding: "0.25rem 0.5rem",
                                marginTop: "0.25rem",
                              }}
                            >
                              {rec.priority} Priority
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Comparison and Next Steps */}
            <Row className="mb-4">
              <Col>
                {/* Next Steps */}
                <Card
                  className="border-0 h-100"
                  style={{
                    backgroundColor: "#13181f",
                    borderRadius: "20px",
                    boxShadow: `0 8px 30px rgba(0, 0, 0, 0.3)`,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <Card.Body style={{ padding: "1.75rem" }}>
                    <h5
                      style={{
                        color: "#ff8214",
                        fontSize: "2.5rem",
                        fontWeight: "600",
                        marginBottom: "1.5rem",
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                      }}
                    >
                      <Clock style={{ color: colors.accent }} />
                      Choose Your Next Step
                    </h5>

                    <div className="d-grid gap-3">
                      {[
                        {
                          id: 1,
                          title: "Request a Call Back",
                          description:
                            "Enter your details and our team will contact you shortly.",
                          icon: <ZoomIn size={24} />,
                          color: colors.success,
                          buttonText: "Call Back Request",
                          features: ["Instant booking", "Basic profile review"],
                        },
                        {
                          id: 2,
                          title: "Office Consultation",
                          description: "Detailed in-person discussion",
                          icon: <Building size={24} />,
                          color: colors.info,
                          buttonText: "Schedule Visit",
                          features: ["45-minute session", "Document analysis"],
                        },
                        // {
                        //   id: 3,
                        //   title: "Comprehensive Paid Assessment 10,000 PKR",
                        //   description: "Full document analysis & strategy",
                        //   icon: <FileEarmarkText size={24} />,
                        //   color: colors.purple,
                        //   buttonText: "Upload Documents",
                        //   features: ["Priority processing", "Expert guidance"],
                        // },
                      ].map((option) => (
                        <div
                          key={option.id}
                          style={{
                            backgroundColor: colors.cardBg,
                            borderRadius: "16px",
                            border: `2px solid ${option.color}40`,
                            padding: "1.25rem",
                            transition: "all 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-3px)";
                            e.currentTarget.style.borderColor = option.color;
                            e.currentTarget.style.boxShadow = `0 8px 25px ${option.color}40`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.borderColor = `${option.color}40`;
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <div className="d-flex align-items-center mb-2">
                            <div
                              style={{
                                color: option.color,
                                marginRight: "12px",
                              }}
                            >
                              {option.icon}
                            </div>
                            <div>
                              <h6
                                style={{
                                  color: colors.text,
                                  fontWeight: "600",
                                  marginBottom: "0.25rem",
                                }}
                              >
                                {option.title}
                              </h6>
                              <p
                                style={{
                                  color: colors.text,
                                  opacity: 0.8,
                                  fontSize: "0.85rem",
                                  marginBottom: "0.5rem",
                                }}
                              >
                                {option.description}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              {option.features.map((feature, idx) => (
                                <Badge
                                  key={idx}
                                  bg=""
                                  style={{
                                    backgroundColor: `${option.color}20`,
                                    color: option.color,
                                    fontSize: "0.75rem",
                                    padding: "0.25rem 0.5rem",
                                    marginRight: "0.25rem",
                                  }}
                                >
                                  {feature}
                                </Badge>
                              ))}
                            </div>
                            <Button
                              onClick={() => handleOptionSelect(option.id)}
                              size="sm"
                              style={{
                                backgroundColor: option.color,
                                borderColor: option.color,
                                color: colors.background,
                                borderRadius: "8px",
                                padding: "0.375rem 1rem",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                              }}
                            >
                              {option.buttonText}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Answer Details Table */}
            {answerDetails.length > 0 && (
              <Card
                className="border-0 mb-4"
                style={{
                  backgroundColor: "#13181f",
                  borderRadius: "20px",
                  boxShadow: `0 8px 30px rgba(0, 0, 0, 0.3)`,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Card.Body style={{ padding: "1.75rem" }}>
                  <h5
                    style={{
                      color: colors.text,
                      fontWeight: "600",
                      marginBottom: "1.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <QuestionCircle style={{ color: colors.accent }} />
                    Your Assessment Answers
                  </h5>
                  <div
                    style={{
                      backgroundColor: colors.background,
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <Table
                      borderless
                      responsive
                      style={{
                        color: colors.text,
                        marginBottom: 0,
                      }}
                    >
                      <thead
                        style={{
                          backgroundColor: colors.cardBg,
                          borderBottom: `2px solid ${colors.border}`,
                        }}
                      >
                        <tr>
                          <th
                            style={{
                              padding: "1rem",
                              fontWeight: "600",
                              borderColor: colors.border,
                            }}
                          >
                            Question
                          </th>
                          <th
                            style={{
                              padding: "1rem",
                              fontWeight: "600",
                              borderColor: colors.border,
                            }}
                          >
                            Your Answer
                          </th>
                          <th
                            style={{
                              padding: "1rem",
                              fontWeight: "600",
                              borderColor: colors.border,
                              textAlign: "center",
                            }}
                          >
                            Points
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {answerDetails.map((answer, index) => (
                          <tr
                            key={index}
                            style={{
                              backgroundColor:
                                index % 2 === 0
                                  ? colors.background
                                  : colors.cardBg,
                              borderBottom: `1px solid ${colors.border}`,
                            }}
                          >
                            <td
                              style={{
                                padding: "1rem",
                                verticalAlign: "top",
                                borderColor: colors.border,
                              }}
                            >
                              <strong>
                                Q{answer.questionId.replace("q", "")}:
                              </strong>{" "}
                              {answer.questionText}
                            </td>
                            <td
                              style={{
                                padding: "1rem",
                                verticalAlign: "top",
                                borderColor: colors.border,
                              }}
                            >
                              {answer.answer}
                            </td>
                            <td
                              style={{
                                padding: "1rem",
                                verticalAlign: "top",
                                borderColor: colors.border,
                                textAlign: "center",
                              }}
                            >
                              <Badge
                                bg=""
                                style={{
                                  backgroundColor:
                                    answer.points === answer.maxPoints
                                      ? colors.success + "30"
                                      : answer.points > answer.maxPoints * 0.5
                                        ? colors.warning + "30"
                                        : colors.danger + "30",
                                  color:
                                    answer.points === answer.maxPoints
                                      ? colors.success
                                      : answer.points > answer.maxPoints * 0.5
                                        ? colors.warning
                                        : colors.danger,
                                  fontSize: "0.9rem",
                                  padding: "0.5rem 1rem",
                                  fontWeight: "600",
                                }}
                              >
                                {answer.points} / {answer.maxPoints}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      {/* PDF Preview Modal */}
      <PDFPreviewModal />

      {/* Custom Styles */}
      <style jsx="true">{`
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: ${colors.background};
        }

        ::-webkit-scrollbar-thumb {
          background: ${colors.accent};
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.accentHover};
        }

        .btn:disabled {
          cursor: not-allowed;
        }

        .progress-bar {
          transition: width 1s ease-in-out;
        }

        .sticky-top {
          position: -webkit-sticky;
          position: sticky;
        }
      `}</style>
    </div>
  );
};

export default ResultsPage;
